import './LineItemsTable.scss';

import { bem } from 'mission-control-common-components';
import React, { useCallback, useMemo } from 'react';

import LineItemsTableHeader from '../../components/LineItemsTableHeader/LineItemsTableHeader';
import useExpandCollapse from '../../hooks/useExpandCollapse';
import type { TagSheetAd, TagSheetParseResult } from '../../types';
import { validateLineItems } from '../../validation/validateLineItems';
import { UNMAPPED_GROUP_ID } from '../constants';
import useLineItemsDragDrop from '../hooks/useLineItemsDragDrop';
import LineItemGroup from './LineItemGroup/LineItemGroup';
import { updateGroupAds, validateLineItemToDrop } from './utils';

const UNMAPPED_GROUP_LABEL = 'Unmapped Ad Records';

const [block, element] = bem('line-items-table');

type LineItemsTableProps = {
  parseResult: TagSheetParseResult;
  setParseResult: React.Dispatch<React.SetStateAction<TagSheetParseResult | null>>;
};

const LineItemsTable = ({ parseResult, setParseResult }: LineItemsTableProps): React.JSX.Element => {
  const { isExpanded, toggleExpand } = useExpandCollapse();

  const handleValidateLineItemToDrop = useCallback(
    (adId: string, lineItemId: string) => validateLineItemToDrop(parseResult, adId, lineItemId),
    [parseResult]
  );

  const dragHandlers = useLineItemsDragDrop(setParseResult, handleValidateLineItemToDrop);
  const { adErrors } = useMemo(() => validateLineItems(parseResult), [parseResult]);

  const getUpdateLineItemGroupCallback = useCallback(
    (groupId: string) => (updatedAds: TagSheetAd[]) => {
      setParseResult((prev) => updateGroupAds(prev, groupId, updatedAds));
    },
    [setParseResult]
  );

  return (
    <div className={block()}>
      <table className={element('table')}>
        <LineItemsTableHeader />
        <LineItemGroup
          groupId={UNMAPPED_GROUP_ID}
          label={UNMAPPED_GROUP_LABEL}
          ads={parseResult.unmappedAds}
          isUnmapped
          isExpanded={isExpanded}
          onToggleExpand={toggleExpand}
          dragHandlers={dragHandlers}
          isDropTarget={dragHandlers.dropTargetGroupId === UNMAPPED_GROUP_ID}
          updateAds={getUpdateLineItemGroupCallback(UNMAPPED_GROUP_ID)}
          adErrors={{}}
        />
        {parseResult.lineItems.map((li) => (
          <LineItemGroup
            key={li.id}
            groupId={li.id}
            label={li.name}
            ads={li.tagSheetAds}
            isUnmapped={false}
            startDate={li.startDate}
            endDate={li.endDate}
            billableEvent={li.billableEvent}
            isExpanded={isExpanded}
            onToggleExpand={toggleExpand}
            dragHandlers={dragHandlers}
            isDropTarget={dragHandlers.dropTargetGroupId === li.id}
            updateAds={getUpdateLineItemGroupCallback(li.id)}
            adErrors={adErrors}
          />
        ))}
      </table>
    </div>
  );
};

export default LineItemsTable;
